import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {parse, stringify} from 'query-string';
import {push as pushAction} from 'react-router-redux';
import {Card, CardText} from 'material-ui/Card';
import compose from 'recompose/compose';
import {createSelector} from 'reselect';
import inflection from 'inflection';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import autoprefixer from 'material-ui/utils/autoprefixer';
import queryReducer, {
    SET_SORT,
    SET_PAGE,
    SET_FILTER,
    SORT_DESC,
} from 'admin-on-rest/lib/reducer/admin/resource/list/queryReducer';
import ViewTitle from 'admin-on-rest/lib/mui/layout/ViewTitle';
import Title from 'admin-on-rest/lib/mui/layout/Title';
import DefaultPagination from 'admin-on-rest/lib/mui/list/Pagination';
import DefaultActions from 'admin-on-rest/lib/mui/list/Actions';
import {crudGetList as crudGetListAction} from 'admin-on-rest/lib/actions/dataActions';
import {changeListParams as changeListParamsAction} from 'admin-on-rest/lib/actions/listActions';
import {refreshView as refreshViewAction} from 'admin-on-rest/lib/actions/uiActions';
import translate from 'admin-on-rest/lib/i18n/translate';
import removeKey from 'admin-on-rest/lib/util/removeKey';
import defaultTheme from 'admin-on-rest/lib/mui/defaultTheme';
import withPermissionsFilteredChildren from 'admin-on-rest/lib/auth/withPermissionsFilteredChildren';
import PageDescription from '../components/PageDescription';
import {LIST_FILTERS} from '../shared/filterLists';
import CircularProgress from 'material-ui/CircularProgress';

const styles = {
    noResults: {padding: 20},
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    loader: {
        display: 'inline-block',
        margin: '0 6px 0 20px'
    }
};

/**
 * List page component (AOR override)
 *
 * The <List> component renders the list layout (title, buttons, filters, pagination),
 * and fetches the list of records from the REST API.
 * It then delegates the rendering of the list of records to its child component.
 * Usually, it's a <Datagrid>, responsible for displaying a table with one row for each post.
 *
 * In Redux terms, <List> is a connected component, and <Datagrid> is a dumb component.
 *
 * Props:
 *   - title
 *   - desc
 *   - perPage
 *   - sort
 *   - filter (the permanent filter to apply to the query)
 *   - actions
 *   - filters (a React Element used to display the filter form)
 *   - pagination
 *
 */
export class List extends Component {
    state = {};

    componentDidMount() {
        let refreshData = false;

        // If a save function updated the resource state with an invalid item, force a reload
        const undefinedId = this.props.ids.findIndex(i => i === undefined);
        const undefinedData = this.props.data['undefined'];
        if (
            (undefinedId !== -1 || undefinedData !== undefined) ||
            !this.props.requiresFilter
        ) {
            refreshData = true;
        }

        // If this List requires filters and a newly created item does not match current filters, force a reload
        if (!refreshData) {
            const resource = this.props.resource;
            const listIds = this.props.ids;
            const filterValues = this.props.filterValues;
            if (resource && LIST_FILTERS[resource] && listIds.length && filterValues) {
                const listData = this.props.data;
                let checks = LIST_FILTERS[resource].checks;
                // Check each item in the data list
                for (let i = 0; i < listIds.length; i++) {
                    let dataItem = listData[listIds[i]];
                    if (dataItem) {
                        // Check each item against the current filter(s)
                        for (let c = 0; c < checks.length; c++) {
                            let dataValue = dataItem[checks[c].data_name];
                            let filterValue = filterValues[checks[c].filter_name];
                            if (filterValue && filterValue !== dataValue) {
                                // If this filter is set and this item's value doesn't match, force a refresh
                                refreshData = true;
                                break;
                            }
                        }
                    }
                }
            }
        }

        if (refreshData) {
            this.updateData();
        }
        if (Object.keys(this.props.query).length > 0) {
            this.props.changeListParams(this.props.resource, this.props.query);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.resource !== this.props.resource ||
            nextProps.query.sort !== this.props.query.sort ||
            nextProps.query.order !== this.props.query.order ||
            nextProps.query.page !== this.props.query.page ||
            nextProps.query.filter !== this.props.query.filter ||
            nextProps.filterValues !== this.props.filterValues
        ) {
            this.updateData(
                Object.keys(nextProps.query).length > 0
                    ? nextProps.query
                    : nextProps.params
            );
        }
        if (nextProps.version !== this.props.version) {
            this.updateData();
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            nextProps.isLoading === this.props.isLoading &&
            nextProps.width === this.props.width &&
            nextProps.version === this.props.version &&
            nextState === this.state &&
            this.props.listType !== 'table'
        ) {
            return false;
        }
        return true;
    }

    getBasePath() {
        return this.props.location.pathname.replace(/\/$/, '');
    }

    /**
     * Merge list params from 3 different sources:
     *   - the query string
     *   - the params stored in the state (from previous navigation)
     *   - the props passed to the List component
     */
    getQuery() {
        const query =
            Object.keys(this.props.query).length > 0
                ? this.props.query
                : {...this.props.params};
        if (!query.sort) {
            query.sort = this.props.sort.field;
            query.order = this.props.sort.order;
        }
        if (!query.perPage) {
            query.perPage = this.props.perPage;
        }
        return query;
    }

    updateData(query) {
        const params = query || this.getQuery();
        const {sort, order, page, perPage, filter} = params;
        const pagination = {
            page: parseInt(page, 10),
            perPage: parseInt(perPage, 10),
        };
        const permanentFilter = this.props.filter;
        this.props.crudGetList(
            this.props.resource,
            pagination,
            {field: sort, order},
            {...filter, ...permanentFilter}
        );
    }

    setSort = sort => this.changeParams({type: SET_SORT, payload: sort});

    setPage = page => this.changeParams({type: SET_PAGE, payload: page});

    setFilters = filters => this.changeParams({type: SET_FILTER, payload: filters});

    showFilter = (filterName, defaultValue) => {
        this.setState({[filterName]: true});
        if (typeof defaultValue !== 'undefined') {
            this.setFilters({
                ...this.props.filterValues,
                [filterName]: defaultValue,
            });
        }
    };

    hideFilter = filterName => {
        this.setState({[filterName]: false});
        const newFilters = removeKey(this.props.filterValues, filterName);
        this.setFilters(newFilters);
    };

    changeParams(action) {
        const newParams = queryReducer(this.getQuery(), action);
        if (this.props.allowQueryString) {
            this.props.push({
                ...this.props.location,
                search: `?${stringify({
                    ...newParams,
                    filter: JSON.stringify(newParams.filter),
                })}`,
            });
        }
        this.props.changeListParams(this.props.resource, newParams);
    }

    refresh = () => {
        if (process.env !== 'production') {
            console.warn( // eslint-disable-line
                'Deprecation warning: The preferred way to refresh the List view is to connect your custom button with redux and dispatch the `refreshView` action.'
            );
        }

        this.props.refreshView();
    };

    render() {
        const {
            children,
            filters,
            pagination,
            actions = <DefaultActions/>,
            resource,
            hasCreate,
            title,
            desc,
            data,
            ids,
            total,
            isLoading,
            translate,
            theme,
            version,
        } = this.props;
        const query = this.getQuery();
        const filterValues = query.filter;
        const basePath = this.getBasePath();

        const resourceName = translate(`resources.${resource}.name`, {
            smart_count: 2,
            _: inflection.humanize(inflection.pluralize(resource)),
        });
        const defaultTitle = translate('aor.page.list', {
            name: `${resourceName}`,
        });
        const titleElement = (
            <Title title={title} defaultTitle={defaultTitle}/>
        );
        const descriptionElement = desc ? (
            <PageDescription desc={desc}/>
        ) : (
            ''
        );
        const muiTheme = getMuiTheme(theme);
        const prefix = autoprefixer(muiTheme);

        return (
            <div className="list-page">
                <Card style={{opacity: isLoading ? 0.8 : 1}}>
                    <div style={prefix(styles.header)}>
                        <ViewTitle title={titleElement}/>
                        {actions &&
                        React.cloneElement(actions, {
                            resource,
                            //filters,
                            //filterValues,
                            basePath,
                            hasCreate,
                            displayedFilters: this.state,
                            //showFilter: this.showFilter,
                            theme,
                            refresh: this.refresh,
                        })}
                    </div>
                    {descriptionElement}
                    {filters &&
                    React.cloneElement(filters, {
                        resource,
                        hideFilter: this.hideFilter,
                        filterValues,
                        displayedFilters: this.state,
                        setFilters: this.setFilters,
                        context: 'form',
                    })}
                    {isLoading &&
                        <span>
                            <CircularProgress
                                style={styles.loader}
                                size={36}
                                thickness={3}
                                className="loader"
                            />
                            Loading
                        </span>
                    }
                    {isLoading || total > 0 ? (
                        <div key={version}>
                            {children &&
                            React.cloneElement(children, {
                                resource,
                                ids,
                                data,
                                currentSort: {
                                    field: query.sort,
                                    order: query.order,
                                },
                                basePath,
                                isLoading,
                                filterValues: filterValues,
                                setSort: this.setSort,
                            })}
                            {pagination &&
                            React.cloneElement(pagination, {
                                total,
                                page: parseInt(query.page, 10),
                                perPage: parseInt(query.perPage, 10),
                                setPage: this.setPage,
                            })}
                        </div>
                    ) : (
                        <CardText style={styles.noResults}>
                            {translate('aor.navigation.no_results')}
                        </CardText>
                    )}
                </Card>
            </div>
        );
    }
}

List.propTypes = {
    // the props you can change
    title: PropTypes.any,
    desc: PropTypes.any,
    filter: PropTypes.object,
    filters: PropTypes.element,
    pagination: PropTypes.element,
    actions: PropTypes.element,
    allowQueryString: PropTypes.bool,
    listType: PropTypes.string,
    perPage: PropTypes.number.isRequired,
    requiresFilter: PropTypes.bool,
    sort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.string,
    }),
    children: PropTypes.node,
    // the props managed by admin-on-rest
    authClient: PropTypes.func,
    changeListParams: PropTypes.func.isRequired,
    crudGetList: PropTypes.func.isRequired,
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    filterValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    hasCreate: PropTypes.bool.isRequired,
    ids: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    path: PropTypes.string,
    params: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    query: PropTypes.object.isRequired,
    resource: PropTypes.string.isRequired,
    refreshView: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
    version: PropTypes.number.isRequired,
};

List.defaultProps = {
    allowQueryString: true,
    filter: {},
    filterValues: {},
    listType: 'aor',
    pagination: <DefaultPagination/>,
    perPage: 10,
    requiresFilter: false,
    sort: {
        field: 'id',
        order: SORT_DESC,
    },
    theme: defaultTheme
};

const getLocationSearch = props => props.location.search;
const getQuery = createSelector(getLocationSearch, locationSearch => {
    const query = parse(locationSearch);
    if (query.filter && typeof query.filter === 'string') {
        query.filter = JSON.parse(query.filter);
    }
    return query;
});

const filterUndefined = (ids, data) => {
    // Assign the list of ids from state
    let filteredIds = ids;
    const idx = filteredIds.findIndex(i => i === 'undefined');
    if (idx > 0) {
        // Currently unused as List refresh should clear ids
        //filteredIds.splice(idx, 1);
    }
    // Copy the data object from state
    let filteredData = JSON.parse(JSON.stringify(data));
    // Delete any undefined keys (due to injection of newly created items by AOR)
    if (filteredData['undefined']) {
        delete filteredData['undefined'];
    }
    return {
        ids: filteredIds,
        data: filteredData
    }
};

function mapStateToProps(state, props) {
    const resourceState = state.admin.resources[props.resource];
    const filteredIdsAndData = filterUndefined(resourceState.list.ids, resourceState.data);
    return {
        query: getQuery(props),
        params: resourceState.list.params,
        ids: filteredIdsAndData.ids,
        total: resourceState.list.total,
        data: filteredIdsAndData.data,
        isLoading: state.admin.loading > 0,
        filterValues: resourceState.list.params.filter,
        version: state.admin.ui.viewVersion,
    };
}

const enhance = compose(
    connect(mapStateToProps, {
        crudGetList: crudGetListAction,
        changeListParams: changeListParamsAction,
        push: pushAction,
        refreshView: refreshViewAction,
    }),
    translate,
    withPermissionsFilteredChildren
);

export default enhance(List);



// WEBPACK FOOTER //
// ./src/Fuse/admin/List.js